/**
 * Analytics Context & Provider
 * Centralized analytics tracking with Mixpanel and Sentry
 */

import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react';
import mixpanel from 'mixpanel-browser';
import * as Sentry from '@sentry/react';

// Analytics Context
const AnalyticsContext = createContext(null);

// Analytics Provider Component
export const AnalyticsProvider = ({ children }) => {
  const mixpanelToken = process.env.REACT_APP_MIXPANEL_TOKEN;
  const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
  const environment = process.env.REACT_APP_ENV || 'development';
  const appVersion = process.env.REACT_APP_VERSION || '1.0.0';

  // Initialize Sentry
  useEffect(() => {
    if (sentryDsn) {
      Sentry.init({
        dsn: sentryDsn,
        environment,
        release: `string-sauce@${appVersion}`,
        tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true,
          }),
        ],
      });
    }
  }, [sentryDsn, environment, appVersion]);

  // Initialize Mixpanel
  useEffect(() => {
    if (mixpanelToken) {
      mixpanel.init(mixpanelToken, {
        debug: environment === 'development',
        track_pageview: false, // We handle this manually
        persistence: 'localStorage',
        ignore_dnt: false,
      });
    }
  }, [mixpanelToken, environment]);

  // Track event to both Mixpanel and Sentry
  const trackEvent = useCallback((eventName, properties = {}) => {
    const enrichedProps = {
      ...properties,
      timestamp: new Date().toISOString(),
      environment,
      app_version: appVersion,
    };

    // Mixpanel
    if (mixpanelToken) {
      mixpanel.track(eventName, enrichedProps);
    }

    // Sentry breadcrumb
    Sentry.addBreadcrumb({
      category: 'analytics',
      message: eventName,
      data: enrichedProps,
      level: 'info',
    });
  }, [mixpanelToken, environment, appVersion]);

  // Track page view
  const trackPageView = useCallback((pageName, pageProps = {}) => {
    trackEvent('page_view', {
      page_name: pageName,
      page_path: window.location.pathname,
      page_url: window.location.href,
      referrer: document.referrer,
      ...pageProps,
    });
  }, [trackEvent]);

  // Identify user
  const identifyUser = useCallback((userId, userProps = {}) => {
    // Mixpanel
    if (mixpanelToken) {
      mixpanel.identify(userId);
      mixpanel.people.set({
        $email: userProps.email,
        $name: userProps.name,
        subscription_tier: userProps.planId || 'free',
        ...userProps,
      });
    }

    // Sentry
    Sentry.setUser({
      id: userId,
      email: userProps.email,
      username: userProps.name,
    });

    trackEvent('user_identified', { user_id: userId });
  }, [mixpanelToken, trackEvent]);

  // Reset user (logout)
  const resetUser = useCallback(() => {
    if (mixpanelToken) {
      mixpanel.reset();
    }
    Sentry.setUser(null);
  }, [mixpanelToken]);

  // Track error
  const trackError = useCallback((error, context = {}) => {
    Sentry.captureException(error, {
      extra: context,
    });
    trackEvent('error_occurred', {
      error_message: error.message,
      error_name: error.name,
      ...context,
    });
  }, [trackEvent]);

  // Time an event (for performance tracking)
  const timeEvent = useCallback((eventName) => {
    if (mixpanelToken) {
      mixpanel.time_event(eventName);
    }
  }, [mixpanelToken]);

  // Track music production specific events
  const trackDAWEvent = useCallback((action, projectData = {}, additionalProps = {}) => {
    trackEvent('daw_action', {
      action,
      project_id: projectData.id,
      project_name: projectData.name,
      track_count: projectData.trackCount,
      ...additionalProps,
    });
  }, [trackEvent]);

  // Track subscription events
  const trackSubscriptionEvent = useCallback((action, planData = {}) => {
    trackEvent('subscription_action', {
      action,
      plan_id: planData.planId,
      plan_name: planData.planName,
      billing_cycle: planData.billingCycle,
    });
  }, [trackEvent]);

  const analytics = useMemo(() => ({
    trackEvent,
    trackPageView,
    identifyUser,
    resetUser,
    trackError,
    timeEvent,
    trackDAWEvent,
    trackSubscriptionEvent,
    mixpanel: mixpanelToken ? mixpanel : null,
    sentry: Sentry,
  }), [
    trackEvent,
    trackPageView,
    identifyUser,
    resetUser,
    trackError,
    timeEvent,
    trackDAWEvent,
    trackSubscriptionEvent,
    mixpanelToken,
  ]);

  return (
    <AnalyticsContext.Provider value={analytics}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Custom hook for using analytics
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    console.error('useAnalytics must be used within an AnalyticsProvider');
    // Return no-op functions if not in provider
    return {
      trackEvent: () => {},
      trackPageView: () => {},
      identifyUser: () => {},
      resetUser: () => {},
      trackError: () => {},
      timeEvent: () => {},
      trackDAWEvent: () => {},
      trackSubscriptionEvent: () => {},
      mixpanel: null,
      sentry: null,
    };
  }
  return context;
};

// Page View Tracker Component (for React Router)
export const PageViewTracker = () => {
  const { trackPageView } = useAnalytics();
  const location = window.location;

  useEffect(() => {
    const pageName = getPageName(location.pathname);
    trackPageView(pageName);
  }, [location.pathname, trackPageView]);

  return null;
};

function getPageName(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'home';
  
  const routeMap = {
    'studio': 'studio',
    'projects': 'projects',
    'pricing': 'pricing',
    'login': 'login',
    'signup': 'signup',
    'settings': 'settings',
    'marketplace': 'marketplace',
  };
  
  return routeMap[segments[0]] || segments[0];
}

export default AnalyticsProvider;
